import { Logger, SingleIndex } from '@deqo/foundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { ActiveObjectWithEvents, resolve } from '../../Container';
import { Physical } from '../../canvas/entities/Physical';
import { ZoneAssocation } from './ZoneAssociations';

const metrics = Logger.get('app.metrics');

export interface ZoneIndexStreams {
    updated: string[];
}

export class ZoneIndex extends ActiveObjectWithEvents<ZoneIndexStreams> {

    @resolve(ObjectManager) private objects!: ObjectManager;

    private readonly forwardIndex = new SingleIndex<string, Physical>();
    private readonly reverseIndex = new Map<Physical, string>();
    private readonly invalidations = new Set<Physical>();

    public start(): void {
        this.rebuild();
        this.objects.on('updated', this.onUpdate);
    }

    public lookup(zoneId: string): ReadonlySet<Physical> {
        this.refresh();
        return this.forwardIndex.get(zoneId);
    }

    private rebuild() {
        const t = performance.now();

        const results = this.objects.findAll(x => x.is(Physical)) as Physical[];
        results.forEach(x => this.add(x));

        metrics.log('ZoneIndex.rebuild', `${performance.now() - t}ms`);
    }

    private refresh() {
        const affectedKeys = new Set<string>();
        for (const p of this.invalidations) {
            const old = this.remove(p);
            if (old) affectedKeys.add(old);

            if (p.alive) {
                const neu = this.add(p);
                if (neu) affectedKeys.add(neu);
            }
        }
        this.invalidations.clear();
        if (affectedKeys.size) {
            this.events.emit('updated', Array.from(affectedKeys));
        }
    }

    private add(p: Physical) {
        const key = ZoneAssocation.get(p);
        if (key !== null && key !== undefined) {
            this.forwardIndex.add(key, p);
            this.reverseIndex.set(p, key);
            return key;
        }
        return null;
    }

    private remove(p: Physical) {
        const key = this.reverseIndex.get(p);
        if (key) {
            this.forwardIndex.remove(key, p);
            this.reverseIndex.delete(p);
            return key;
        }
        return null;
    }

    private onUpdate = (affected: Component[]) => {
        for (const c of affected) {
            if (c.is(Physical)) {
                this.invalidations.add(c as any);
            }
        }
        this.refresh();
    };
}
